// src/pages/CancerTeleconsultationSpoke.jsx
import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  Video, 
  CheckCircle2, 
  ArrowRight, 
  Clock, 
  DollarSign, 
  ChevronRight, 
  ChevronDown,
  Activity,
  FileSearch,
  UploadCloud,
  ArrowLeft,
  FileText,
  Lock,
  Sparkles
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';
import { TELECONSULTATION_HUB_DATA, TELECONSULTATION_SPOKES } from '@/data/teleconsultationData';
import Footer from '@/components/Footer';

export default function CancerTeleconsultationSpoke({ slugOverride }) {
  const params = useParams();
  const spokeSlug = slugOverride || params.spokeSlug;
  const [openFaq, setOpenFaq] = useState(null);

  const spoke = TELECONSULTATION_SPOKES.find((s) => s.slug === spokeSlug);

  if (!spoke) {
    return <Navigate to="/cancer-teleconsultation" replace />;
  }

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const pageUrl = `${SITE_ORIGIN}${spoke.path}`;
  const hubUrl = `${SITE_ORIGIN}/cancer-teleconsultation`;

  const relatedSpokeObjects = spoke.relatedSpokes
    ? spoke.relatedSpokes.map((relSlug) => TELECONSULTATION_SPOKES.find((s) => s.slug === relSlug)).filter(Boolean)
    : [];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: spoke.title,
      description: spoke.description,
      url: pageUrl,
      about: {
        '@type': 'MedicalCondition',
        name: spoke.shortName,
      },
      author: {
        '@type': 'Person',
        name: spoke.reviewer.name,
        jobTitle: spoke.reviewer.title,
        description: spoke.reviewer.credentials,
      },
      reviewedBy: {
        '@type': 'Person',
        name: spoke.reviewer.name,
        jobTitle: spoke.reviewer.title,
      },
      lastReviewed: '2026-09-24',
      provider: {
        '@type': 'MedicalOrganization',
        name: 'ByOnco Oncology Telemedicine Network',
        url: SITE_ORIGIN,
        logo: `${SITE_ORIGIN}/byonco-logo.png`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Cancer Teleconsultation', item: hubUrl },
        { '@type': 'ListItem', position: 3, name: spoke.shortName, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: spoke.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-charcoal font-sans antialiased">
      <MetaTags
        title={spoke.metaTitle}
        description={spoke.description}
        path={spoke.path}
        jsonLd={jsonLd}
      />

      {/* Top Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="border-b border-border-mist bg-white/60 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between text-xs sm:text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-forest-ink transition">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/cancer-teleconsultation" className="text-gray-500 hover:text-forest-ink transition">
                Teleconsultation Hub
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-forest-ink font-semibold" aria-current="page">{spoke.shortName}</li>
          </ol>
          <div className="flex items-center gap-3">
            <Link 
              to="/teleconsultation" 
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-ink text-white text-xs font-medium hover:bg-forest-shadow transition"
            >
              Book Video Visit ($199) <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-12 pb-16 bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-6">
            <Video className="w-4 h-4 text-emerald-700" />
            Specialized Video Visit · {spoke.shortName}
          </div>
          
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold font-serif text-forest-ink tracking-tight mb-4 leading-tight">
            {spoke.h1}
          </h1>
          <p className="text-lg sm:text-2xl text-emerald-800 font-medium mb-6">
            {spoke.h1Sub}
          </p>
          <p className="max-w-3xl mx-auto text-base sm:text-lg text-charcoal/90 leading-relaxed mb-8">
            {spoke.clinicalSummary}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Link
              to="/teleconsultation"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-forest-ink hover:bg-forest-shadow text-white font-semibold text-base transition shadow-md hover:shadow-lg"
            >
              Book Video Visit ($199) <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/cancer-teleconsultation"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-gray-300 hover:border-forest-ink bg-white text-forest-ink font-semibold text-base transition"
            >
              <ArrowLeft className="w-4 h-4" /> View All Teleconsultations
            </Link>
          </div>

          {/* Quick Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-4xl mx-auto text-left">
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Clock className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Scheduling</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">Within 24–48 Hours</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Video className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Consultation</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">30–45 Min Video</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <Lock className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Security</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">HIPAA Encrypted</p>
              </div>
            </div>
            <div className="p-3.5 rounded-lg bg-white border border-border-mist shadow-xs flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-emerald-700 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Flat Fee</p>
                <p className="text-xs sm:text-sm font-semibold text-forest-ink">$199 Upfront</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Medical Review Byline */}
      <section className="bg-white border-b border-border-mist py-5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs sm:text-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-800 font-bold text-sm">
                MD
              </div>
              <div>
                <p className="font-semibold text-forest-ink">
                  Medically Reviewed by {spoke.reviewer.name}
                </p>
                <p className="text-gray-600 text-xs">
                  {spoke.reviewer.title} · {spoke.reviewer.credentials}
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right text-gray-500 text-xs">
              <p>Updated: {spoke.reviewer.lastReviewed}</p>
              <p className="text-emerald-800 font-medium">{spoke.reviewer.guidelineRef}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">

        {/* Section 1: Key Topics Covered */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-3 py-1 rounded-full">
              Clinical Discussion Points
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mt-3 mb-3">
              Core Areas Evaluated During Your Video Visit
            </h2>
            <p className="text-charcoal/80 text-base">
              Our oncologists review your specific disease biology, staging milestones, and genomic profiling to provide personalized, actionable clarity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {spoke.keyTopics.map((item, idx) => (
              <div key={idx} className="p-6 rounded-xl bg-white border border-border-mist hover:border-emerald-300 transition shadow-xs">
                <div className="flex items-center gap-2.5 mb-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                  <h3 className="font-bold text-forest-ink text-base">{item.title}</h3>
                </div>
                <p className="text-xs sm:text-sm text-charcoal/80 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: What to Prepare */}
        <section className="bg-white p-8 sm:p-10 rounded-2xl border border-border-mist shadow-xs">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8 pb-6 border-b border-border-mist">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-2">
                What to Prepare Before Your Video Visit
              </h2>
              <p className="text-charcoal/80 text-sm sm:text-base">
                Preparing these records in advance allows your oncologist to dive straight into strategy and clinical recommendations.
              </p>
            </div>
            <Link
              to="/teleconsultation"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-forest-ink text-white font-medium text-sm hover:bg-forest-shadow transition flex-shrink-0"
            >
              <UploadCloud className="w-4 h-4" /> Upload Files
            </Link>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-cream-paper/60 border border-border-mist">
              <FileText className="w-5 h-5 text-emerald-800 mb-2" />
              <h3 className="font-semibold text-sm text-forest-ink mb-1">Pathology Reports</h3>
              <p className="text-xs text-charcoal/70">Biopsy results, immunohistochemistry (IHC) markers, and surgical operative records.</p>
            </div>
            <div className="p-4 rounded-xl bg-cream-paper/60 border border-border-mist">
              <FileSearch className="w-5 h-5 text-emerald-800 mb-2" />
              <h3 className="font-semibold text-sm text-forest-ink mb-1">Scan Reports & DICOM</h3>
              <p className="text-xs text-charcoal/70">CT, MRI, or PET-CT reports plus raw imaging discs for cross-sectional screen share review.</p>
            </div>
            <div className="p-4 rounded-xl bg-cream-paper/60 border border-border-mist">
              <Activity className="w-5 h-5 text-emerald-800 mb-2" />
              <h3 className="font-semibold text-sm text-forest-ink mb-1">Your Questions List</h3>
              <p className="text-xs text-charcoal/70">5 to 7 prioritized questions regarding treatment goals, toxicity risks, and trial options.</p>
            </div>
          </div>
        </section>

        {/* Section 3: FAQs */}
        <section>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold font-serif text-forest-ink mb-3">
              Frequently Asked Questions: {spoke.shortName}
            </h2>
            <p className="text-charcoal/80 text-base">
              Clear clinical answers regarding visit expectations, record preparation, and treatment continuity.
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {spoke.faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div 
                  key={idx}
                  className="rounded-xl border border-border-mist bg-white overflow-hidden transition shadow-xs"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full text-left p-5 sm:p-6 flex items-center justify-between gap-4 font-bold text-base sm:text-lg text-forest-ink hover:text-emerald-800 transition"
                    aria-expanded={isOpen}
                  >
                    <span>{faq.question}</span>
                    {isOpen ? (
                      <ChevronDown className="w-5 h-5 text-emerald-700 flex-shrink-0 rotate-180 transition-transform" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 transition-transform" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-6 sm:px-6 text-sm sm:text-base text-charcoal/85 leading-relaxed border-t border-cream-paper bg-cream-paper/30">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 4: Cross-links & Related Services */}
        <section className="bg-white p-8 sm:p-10 rounded-2xl border border-border-mist shadow-xs">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-border-mist">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold font-serif text-forest-ink">
                Related Oncology Virtual Services
              </h2>
              <p className="text-xs sm:text-sm text-charcoal/80">
                Explore adjacent clinical visit guides and multidisciplinary second opinions.
              </p>
            </div>
            <Link
              to="/cancer-teleconsultation"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-emerald-800 hover:text-forest-ink transition flex-shrink-0"
            >
              All Teleconsultations <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {relatedSpokeObjects.map((relSpoke) => (
              <Link
                key={relSpoke.slug}
                to={relSpoke.path}
                className="p-4 rounded-xl border border-border-mist hover:border-emerald-500 hover:bg-emerald-50/30 transition flex flex-col justify-between"
              >
                <div>
                  <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                    {relSpoke.shortName}
                  </span>
                  <h3 className="font-bold text-forest-ink text-sm mt-1 mb-2">
                    {relSpoke.h1}
                  </h3>
                  <p className="text-xs text-charcoal/70 line-clamp-2">
                    {relSpoke.clinicalSummary}
                  </p>
                </div>
                <div className="mt-3 pt-2 border-t border-border-mist flex items-center text-xs font-semibold text-emerald-800">
                  <span>Explore Guide</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </div>
              </Link>
            ))}

            {/* Cross link to Cancer Second Opinion counterpart */}
            <Link
              to="/cancer-second-opinion"
              className="p-4 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50 transition flex flex-col justify-between"
            >
              <div>
                <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wide">
                  Pathology & Scans
                </span>
                <h3 className="font-bold text-forest-ink text-sm mt-1 mb-2">
                  Multidisciplinary Second Opinion
                </h3>
                <p className="text-xs text-charcoal/70">
                  Need a full tumor board slide re-review or DICOM over-read? Explore our written multidisciplinary second opinion panel.
                </p>
              </div>
              <div className="mt-3 pt-2 border-t border-emerald-200 flex items-center text-xs font-semibold text-emerald-900">
                <span>View Second Opinion Hub</span>
                <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </div>
            </Link>
          </div>
        </section>

        {/* Section 5: Contextual CTA */}
        <section className="bg-gradient-to-r from-forest-ink to-emerald-950 text-white rounded-3xl p-8 sm:p-12 text-center shadow-xl">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-4xl font-bold font-serif text-white mb-4 leading-tight">
              Schedule Your {spoke.shortName} Today
            </h2>
            <p className="text-emerald-100/90 text-sm sm:text-base mb-8 max-w-2xl mx-auto leading-relaxed">
              Connect directly with a board-certified oncologist. Complete your intake in 3 minutes, upload your records, and receive clarity within 24 to 48 hours for a transparent $199 flat fee.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/teleconsultation"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-white text-forest-ink hover:bg-emerald-50 font-bold text-base transition shadow-md"
              >
                Book Video Visit ($199) <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/cancer-teleconsultation"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-white/30 hover:border-white text-white font-medium text-base transition"
              >
                Back to Teleconsultation Hub
              </Link>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
